import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import jsPDF from 'jspdf';
import { supabase } from '../supabaseClient';
import { nombreSeguroStorage } from '../lib/nombre-storage';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { SGA_CLASSES } from '../lib/sgaClasses';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SGALabel {
  id: string;
  company_id: string;
  product_id: string;
  signal_word: 'PELIGRO' | 'ATENCIÓN';
  selected_h_phrases: string[];
  selected_p_phrases: string[];
  created_by: string | null;
  created_at: string;
  product_name: string;
  product_sga_classes: string[];
}

interface HazardPhrase {
  id: string;
  sga_class_id: string;
  phrase_code: string;
  phrase_text: string;
  phrase_type: 'H' | 'P';
}

interface ProductOption {
  id: string;
  name: string;
  sga_classes: string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function imgToDataUrl(src: string, size = 64): Promise<string> {
  const loadImg = (url: string): Promise<string> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = size; c.height = size;
        const ctx = c.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, size, size);
          resolve(c.toDataURL('image/png'));
        } else {
          resolve('');
        }
      };
      img.onerror = () => resolve('');
      img.src = url;
    });

  if (src.endsWith('.svg') || src.startsWith('data:image/svg')) {
    return fetch(src)
      .then(r => r.text())
      .then(svgText => {
        const blob = new Blob([svgText], { type: 'image/svg+xml' });
        const blobUrl = URL.createObjectURL(blob);
        return loadImg(blobUrl).finally(() => URL.revokeObjectURL(blobUrl));
      })
      .catch(() => '');
  }

  return loadImg(src);
}

function mensajeDeError(e: unknown): string {
  return (e as { message?: string } | null)?.message ?? String(e);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SGALabelsPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const canEdit   = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  const [labels,  setLabels]  = useState<SGALabel[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [phrases, setPhrases] = useState<HazardPhrase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // Modal crear
  const [showModal,    setShowModal]    = useState(false);
  const [selProductId, setSelProductId] = useState('');
  const [signalWord,   setSignalWord]   = useState<'PELIGRO' | 'ATENCIÓN'>('PELIGRO');
  const [selectedH,    setSelectedH]    = useState<string[]>([]);
  const [selectedP,    setSelectedP]    = useState<string[]>([]);
  const [saving,       setSaving]       = useState(false);

  // Eliminar (2 pasos)
  const [deleteTarget,  setDeleteTarget]  = useState<SGALabel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  // PDF
  const [generatingPdf, setGeneratingPdf] = useState<string | null>(null);

  // ── Data loading ───────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [labelsRes, productsRes, phrasesRes] = await Promise.all([
      supabase
        .from('sga_labels')
        .select('*, products(name, sga_classes)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false }),
      supabase
        .from('products')
        .select('id, name, sga_classes')
        .eq('company_id', companyId)
        .eq('status', 'active')
        .order('name'),
      supabase
        .from('sga_hazard_phrases')
        .select('*')
        .order('phrase_code'),
    ]);

    if (labelsRes.error)   { setError('Error al cargar etiquetas: '  + labelsRes.error.message);   setLoading(false); return; }
    if (productsRes.error) { setError('Error al cargar productos: '  + productsRes.error.message); setLoading(false); return; }
    if (phrasesRes.error)  { setError('Error al cargar frases H/P: ' + phrasesRes.error.message);  setLoading(false); return; }

    const mapped: SGALabel[] = (labelsRes.data ?? []).map((l: any) => ({
      id:                   l.id,
      company_id:           l.company_id,
      product_id:           l.product_id,
      signal_word:          l.signal_word,
      selected_h_phrases:   l.selected_h_phrases ?? [],
      selected_p_phrases:   l.selected_p_phrases ?? [],
      created_by:           l.created_by,
      created_at:           l.created_at,
      product_name:         l.products?.name ?? '(producto eliminado)',
      product_sga_classes:  l.products?.sga_classes ?? [],
    }));

    setLabels(mapped);
    setProducts(productsRes.data ?? []);
    setPhrases(phrasesRes.data ?? []);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Derived from modal selection ───────────────────────────────────────────

  const selProduct  = products.find(p => p.id === selProductId) ?? null;
  const selClasses  = selProduct ? SGA_CLASSES.filter(c => selProduct.sga_classes.includes(c.id)) : [];
  const hPhrases    = phrases.filter(p => p.phrase_type === 'H' && (selProduct?.sga_classes ?? []).includes(p.sga_class_id));
  const pPhrases    = phrases.filter(p => p.phrase_type === 'P' && (selProduct?.sga_classes ?? []).includes(p.sga_class_id));

  // ── Handlers ───────────────────────────────────────────────────────────────

  function openModal() {
    setSelProductId('');
    setSignalWord('PELIGRO');
    setSelectedH([]);
    setSelectedP([]);
    setShowModal(true);
  }

  function togglePhrase(list: string[], setList: (v: string[]) => void, text: string) {
    setList(list.includes(text) ? list.filter(x => x !== text) : [...list, text]);
  }

  async function handleSave() {
    if (!selProductId || !companyId) return;
    setSaving(true);
    const { error: saveErr } = await supabase.from('sga_labels').insert({
      company_id:         companyId,
      product_id:         selProductId,
      signal_word:        signalWord,
      selected_h_phrases: selectedH,
      selected_p_phrases: selectedP,
    });
    if (saveErr) { alert('Error al guardar etiqueta: ' + saveErr.message); setSaving(false); return; }
    setSaving(false);
    setShowModal(false);
    loadData();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error: delErr } = await supabase.from('sga_labels').delete().eq('id', deleteTarget.id);
    if (delErr) { alert('Error al eliminar etiqueta: ' + delErr.message); setDeleting(false); return; }
    setDeleting(false);
    setDeleteTarget(null);
    loadData();
  }

  async function handlePrint(label: SGALabel) {
    if (!label.product_name.trim()) {
      alert('No se puede generar la etiqueta: falta el nombre del producto.');
      return;
    }
    if (label.selected_h_phrases.length === 0) {
      alert('No se puede generar la etiqueta: selecciona al menos una frase de peligro (H).');
      return;
    }
    setGeneratingPdf(label.id);
    try {
      const doc = new jsPDF({ unit: 'mm', format: [70, 100], orientation: 'landscape' });
      const margin = 4;
      const pageW  = 100;

      // Borde exterior
      doc.setDrawColor(180, 180, 180);
      doc.rect(2, 2, 96, 66);

      // Nombre del producto
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(20, 20, 20);
      const nameLines = doc.splitTextToSize(label.product_name, pageW - margin * 2);
      doc.text(nameLines, margin, 10);

      // Línea divisoria
      const lineY = 10 + nameLines.length * 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, lineY, pageW - margin, lineY);

      // Pictogramas SGA
      const picY    = lineY + 3;
      const picSize = 11;
      const picGap  = 13;
      const failedPictograms: string[] = [];
      const classes = SGA_CLASSES.filter(c => label.product_sga_classes.includes(c.id));
      let picX = margin;
      for (const cls of classes) {
        if (picX + picSize > pageW - margin) break;
        const dataUrl = await imgToDataUrl(cls.svg, 128);
        if (dataUrl) {
          try { doc.addImage(dataUrl, 'PNG', picX, picY, picSize, picSize); } catch (_) { failedPictograms.push(cls.label); }
        }
        picX += picGap;
      }

      // Palabra de advertencia
      const swY = picY + picSize + 6;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      if (label.signal_word === 'PELIGRO') {
        doc.setTextColor(220, 38, 38);
      } else {
        doc.setTextColor(234, 88, 12);
      }
      doc.text(label.signal_word, pageW / 2, swY, { align: 'center' });

      // Frases H
      let curY = swY + 7;
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      if (label.selected_h_phrases.length > 0) {
        doc.text('Indicaciones de peligro:', margin, curY);
        curY += 4;
        doc.setFont('helvetica', 'normal');
        for (const phrase of label.selected_h_phrases) {
          if (curY > 56) break;
          const lines = doc.splitTextToSize('• ' + phrase, pageW - margin * 2);
          doc.text(lines, margin, curY);
          curY += lines.length * 3.2;
        }
      }

      // Frases P
      if (label.selected_p_phrases.length > 0 && curY < 57) {
        curY += 1;
        doc.setFont('helvetica', 'bold');
        doc.text('Medidas de precaución:', margin, curY);
        curY += 4;
        doc.setFont('helvetica', 'normal');
        for (const phrase of label.selected_p_phrases) {
          if (curY > 62) break;
          const lines = doc.splitTextToSize('• ' + phrase, pageW - margin * 2);
          doc.text(lines, margin, curY);
          curY += lines.length * 3.2;
        }
      }

      // Footer
      doc.setFontSize(4.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(160, 160, 160);
      doc.text('Generado por StorSafe', pageW / 2, 67, { align: 'center' });

      const safeName = label.product_name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '_');
      const blob = doc.output('blob');
      doc.save(`Etiqueta_${safeName}.pdf`);
      if (failedPictograms.length > 0) {
        alert(`Algunos pictogramas no pudieron incluirse en el PDF: ${failedPictograms.join(', ')}`);
      }

      // Solo admin, manager o superadmin (impersonando) guardan la etiqueta en StorSafe:
      // la política labels_update no deja actualizar al viewer, que solo la descarga.
      if (canEdit) {
        // El PDF ya se descargó; si falla el guardado en StorSafe se avisa al final
        // sin bloquear. Sin este registro, GT-13 no puede marcarse automáticamente.
        let avisoRegistro: string | null = null;
        try {
          const path = `${label.company_id}/sga-labels/${nombreSeguroStorage(`${label.id}.pdf`)}`;
          let upErr = (await supabase.storage.from('sso-documents')
            .upload(path, blob, { upsert: false, contentType: 'application/pdf' })).error;

          if (upErr) {
            const { error: updateErr } = await supabase.storage.from('sso-documents')
              .update(path, blob, { contentType: 'application/pdf' });
            if (updateErr) throw updateErr;
          }

          const { data: pub } = supabase.storage.from('sso-documents').getPublicUrl(path);
          const { data: updRows, error: updErr } = await supabase
            .from('sga_labels')
            .update({ pdf_url: pub.publicUrl })
            .eq('id', label.id)
            .select('id');
          if (updErr) throw updErr;
          // Con RLS, un UPDATE sin política que lo permita no da error: afecta 0 filas.
          if (!updRows || updRows.length === 0) throw new Error('la etiqueta no se pudo actualizar (sin permiso de edición)');

          try {
            const { data: existingDoc } = await supabase
              .from('sso_documents')
              .select('id')
              .eq('company_id', label.company_id)
              .in('file_url', [pub.publicUrl, path])
              .maybeSingle();

            if (!existingDoc) {
              const { error: docErr } = await supabase.from('sso_documents').insert({
                company_id:  label.company_id,
                category:    'otro',
                title:       `Etiqueta SGA - ${label.product_name}`,
                description: 'Generado automáticamente desde Etiquetado SGA',
                file_url:    pub.publicUrl,
                file_name:   `${label.id}.pdf`,
                uploaded_by: profile?.id ?? null,
                version:     1,
                is_current:  true,
                checklist_item_id: 'GT-13', // Etiquetado de agentes químicos en español (ítem legal GT 16 del Anexo 1)
              });
              if (docErr) throw docErr;
            }
          } catch (docErr) {
            console.warn('No se pudo registrar el documento en sso_documents:', docErr);
            avisoRegistro = 'no se pudo registrar en Documentos SSO (' + mensajeDeError(docErr) + ')';
          }
        } catch (err) {
          console.warn('No se pudo persistir pdf_url en sga_labels:', err);
          avisoRegistro = 'no se pudo guardar el PDF en StorSafe (' + mensajeDeError(err) + ')';
        }
        if (avisoRegistro) {
          alert(`La etiqueta se descargó, pero ${avisoRegistro}.\n\nEl checklist (GT-13) no la contará hasta que se guarde. Intente imprimirla de nuevo; si el problema sigue, avise a soporte.`);
        }
      }
    } catch (e) {
      alert('Error al generar PDF. Intente de nuevo.');
    } finally {
      setGeneratingPdf(null);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>Cargando etiquetas…</div>
  );

  if (error) return (
    <div style={{ padding: 40, color: '#ef4444' }}>{error}</div>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ color: '#f1f5f9', fontSize: '1.45rem', fontWeight: 700, margin: 0 }}>Etiquetado SGA</h1>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 4 }}>
            Genera etiquetas de peligro con frases H/P según clasificación SGA del producto.
          </p>
        </div>
        {canEdit && (
          <button onClick={openModal} style={{
            padding: '9px 18px', background: 'linear-gradient(135deg,#1D6FA3,#2FBF71)',
            color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600,
            fontSize: '0.88rem', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            + Nueva etiqueta
          </button>
        )}
      </div>

      {/* Tabla */}
      {labels.length === 0 ? (
        <div style={{
          background: '#1e293b', border: '1px dashed #334155', borderRadius: 12,
          padding: '60px 24px', textAlign: 'center', color: '#475569',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🏷️</div>
          <p style={{ margin: 0 }}>No hay etiquetas generadas aún.</p>
          {canEdit && <p style={{ margin: '8px 0 0', fontSize: '0.82rem' }}>Haz clic en "Nueva etiqueta" para comenzar.</p>}
        </div>
      ) : (
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Producto', 'Pictogramas', 'Señal', 'Frases H', 'Frases P', 'Fecha', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e293b' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((lbl, i) => {
                const classes = SGA_CLASSES.filter(c => lbl.product_sga_classes.includes(c.id));
                return (
                  <tr key={lbl.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #1e293b' }}>
                    <td style={{ padding: '12px 14px', color: '#e2e8f0', fontSize: '0.88rem', fontWeight: 500, maxWidth: 200 }}>{lbl.product_name}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {classes.slice(0, 5).map(c => (
                          <img key={c.id} src={c.svg} alt={c.label} title={c.label}
                            style={{ width: 26, height: 26, objectFit: 'contain' }} />
                        ))}
                        {classes.length > 5 && (
                          <span style={{ fontSize: '0.72rem', color: '#64748b', alignSelf: 'center' }}>+{classes.length - 5}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: 5,
                        fontSize: '0.72rem', fontWeight: 700,
                        background: lbl.signal_word === 'PELIGRO' ? 'rgba(239,68,68,0.15)' : 'rgba(234,88,12,0.15)',
                        color:      lbl.signal_word === 'PELIGRO' ? '#ef4444' : '#ea580c',
                      }}>
                        {lbl.signal_word}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#94a3b8', fontSize: '0.78rem' }}>
                      {lbl.selected_h_phrases.length} frase{lbl.selected_h_phrases.length !== 1 ? 's' : ''}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#94a3b8', fontSize: '0.78rem' }}>
                      {lbl.selected_p_phrases.length} frase{lbl.selected_p_phrases.length !== 1 ? 's' : ''}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {fmtDate(lbl.created_at)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => handlePrint(lbl)}
                          disabled={generatingPdf === lbl.id}
                          style={{
                            padding: '5px 10px', background: 'rgba(59,130,246,0.12)',
                            color: '#3b82f6', border: '1px solid rgba(59,130,246,0.25)',
                            borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 500,
                          }}
                        >
                          {generatingPdf === lbl.id ? 'Generando…' : 'Ver / Imprimir'}
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => { setDeleteTarget(lbl); setDeleteConfirm(false); }}
                            style={{
                              padding: '5px 10px', background: 'rgba(239,68,68,0.1)',
                              color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)',
                              borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer', fontWeight: 500,
                            }}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal crear etiqueta ──────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 200, padding: 16,
        }}>
          <div style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: 14,
            padding: 28, width: '100%', maxWidth: 640,
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '1.1rem', fontWeight: 700, margin: '0 0 20px' }}>
              Nueva etiqueta SGA
            </h2>

            {/* Selector de producto */}
            <label style={{ display: 'block', marginBottom: 14 }}>
              <span style={{ color: '#94a3b8', fontSize: '0.82rem', display: 'block', marginBottom: 5 }}>Producto *</span>
              <SelectBuscable
                value={selProductId}
                onChange={v => { setSelProductId(v); setSelectedH([]); setSelectedP([]); }}
                inputStyle={{
                  width: '100%', padding: '9px 32px 9px 11px', background: '#0f172a',
                  border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.88rem',
                }}
                vacio="— Selecciona un producto —"
                opciones={products.map(p => ({ value: p.id, label: p.name }))}
              />
            </label>

            {/* Clases SGA del producto */}
            {selProduct && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '0 0 8px' }}>
                  Clasificación SGA del producto:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {selClasses.length === 0 ? (
                    <span style={{ color: '#475569', fontSize: '0.82rem' }}>Sin clases SGA registradas</span>
                  ) : selClasses.map(c => (
                    <div key={c.id} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', background: c.bg, borderRadius: 6,
                      border: `1px solid ${c.color}33`,
                    }}>
                      <img src={c.svg} alt={c.label} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                      <span style={{ color: c.color, fontSize: '0.75rem', fontWeight: 600 }}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Palabra de advertencia */}
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ color: '#94a3b8', fontSize: '0.82rem', display: 'block', marginBottom: 5 }}>Palabra de advertencia *</span>
              <div style={{ display: 'flex', gap: 10 }}>
                {(['PELIGRO', 'ATENCIÓN'] as const).map(w => (
                  <button
                    key={w}
                    onClick={() => setSignalWord(w)}
                    style={{
                      flex: 1, padding: '9px', borderRadius: 8,
                      border: signalWord === w
                        ? `2px solid ${w === 'PELIGRO' ? '#ef4444' : '#ea580c'}`
                        : '2px solid #334155',
                      background: signalWord === w
                        ? (w === 'PELIGRO' ? 'rgba(239,68,68,0.12)' : 'rgba(234,88,12,0.12)')
                        : 'transparent',
                      color: signalWord === w
                        ? (w === 'PELIGRO' ? '#ef4444' : '#ea580c')
                        : '#64748b',
                      fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                    }}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </label>

            {/* Frases H */}
            {selProduct && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '0 0 8px' }}>
                  Indicaciones de peligro (H) — {hPhrases.length} disponibles para las clases del producto:
                </p>
                {hPhrases.length === 0 ? (
                  <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0 }}>
                    No hay frases H registradas para estas clases SGA.
                  </p>
                ) : (
                  <div style={{
                    maxHeight: 180, overflowY: 'auto', padding: '10px 12px',
                    background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    {hPhrases.map(ph => (
                      <label key={ph.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedH.includes(ph.phrase_text)}
                          onChange={() => togglePhrase(selectedH, setSelectedH, ph.phrase_text)}
                          style={{ marginTop: 2, accentColor: '#ef4444', flexShrink: 0 }}
                        />
                        <span style={{ color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.4 }}>
                          <strong style={{ color: '#f87171' }}>{ph.phrase_code}</strong>: {ph.phrase_text}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Frases P */}
            {selProduct && (
              <div style={{ marginBottom: 22 }}>
                <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '0 0 8px' }}>
                  Medidas de precaución (P) — {pPhrases.length} disponibles:
                </p>
                {pPhrases.length === 0 ? (
                  <p style={{ color: '#475569', fontSize: '0.8rem', margin: 0 }}>
                    No hay frases P registradas para estas clases SGA.
                  </p>
                ) : (
                  <div style={{
                    maxHeight: 180, overflowY: 'auto', padding: '10px 12px',
                    background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
                    display: 'flex', flexDirection: 'column', gap: 6,
                  }}>
                    {pPhrases.map(ph => (
                      <label key={ph.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedP.includes(ph.phrase_text)}
                          onChange={() => togglePhrase(selectedP, setSelectedP, ph.phrase_text)}
                          style={{ marginTop: 2, accentColor: '#3b82f6', flexShrink: 0 }}
                        />
                        <span style={{ color: '#cbd5e1', fontSize: '0.8rem', lineHeight: 1.4 }}>
                          <strong style={{ color: '#60a5fa' }}>{ph.phrase_code}</strong>: {ph.phrase_text}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Botones */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1, padding: '10px', background: 'transparent',
                  border: '1px solid #334155', color: '#94a3b8',
                  borderRadius: 8, fontSize: '0.88rem', cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !selProductId}
                style={{
                  flex: 2, padding: '10px',
                  background: 'linear-gradient(135deg,#1D6FA3,#2FBF71)',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer',
                  opacity: (saving || !selProductId) ? 0.6 : 1,
                }}
              >
                {saving ? 'Guardando…' : 'Generar etiqueta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal eliminar (2 pasos) ──────────────────────────────────────── */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 200, padding: 16,
        }}>
          <div style={{
            background: '#1e293b', border: '1px solid #334155', borderRadius: 14,
            padding: 28, width: '100%', maxWidth: 420,
          }}>
            {!deleteConfirm ? (
              <>
                <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 10px' }}>
                  ¿Eliminar esta etiqueta?
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: '0 0 20px' }}>
                  Se eliminará la etiqueta de <strong style={{ color: '#e2e8f0' }}>{deleteTarget.product_name}</strong>.
                  Esta acción no se puede deshacer.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={() => setDeleteConfirm(true)} style={{ flex: 1, padding: '10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Continuar</button>
                </div>
              </>
            ) : (
              <>
                <h2 style={{ color: '#ef4444', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 10px' }}>
                  Confirmar eliminación
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: '0 0 20px' }}>
                  ¿Estás seguro de que deseas eliminar esta etiqueta? No se puede recuperar.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, padding: '10px', background: '#ef4444', border: 'none', color: '#fff', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: deleting ? 0.7 : 1 }}>
                    {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
